import { forwardRef, type ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { CaptionStyle, ClipFormat, ClipOverlay, CropPath, ExportRecord, LockedCall, PostCopy } from './clipFactoryApi'
import {
  cropCentreAt,
  cropObjectX,
  formatTime,
  keptElapsed,
  postCopyText,
  PRESETS,
  type Preset,
  type Range,
} from './clipUtils'
import PostPanel, { type PostTarget } from './PostPanel'
import PreviewOverlay from './PreviewOverlay'

export interface ClipPreviewHandle {
  /** Play `range` of the video from its start, stopping at its end. */
  play: (range: Range) => void
}

/** The panel's own settings; the hook text and receipt are per clip and passed separately. */
export interface PanelOptions {
  format: ClipFormat
  captions: boolean
  captionStyle: CaptionStyle
  chart: boolean
  watermark: boolean
  introOutro: boolean
  hookOn: boolean
}

/**
 * Where the selected clip's export stands for the current settings:
 * idle (never exported), busy, ready (finished, these settings), stale
 * (finished with other settings) or failed.
 */
export type ExportView =
  | { state: 'idle' }
  | { state: 'busy'; record: ExportRecord | null }
  | { state: 'ready'; record: ExportRecord }
  | { state: 'stale'; record: ExportRecord }
  | { state: 'failed'; record: ExportRecord }

interface ClipPreviewProps {
  mediaUrl: string | null
  title: string | null
  range: Range | null
  postCopy: PostCopy | undefined
  /** A rendered "Create a video": no shape, caption, branding or receipt options. */
  created: boolean
  options: PanelOptions
  onOptions: (change: Partial<PanelOptions>) => void
  hookText: string
  onHookText: (text: string) => void
  matches: LockedCall[]
  confirmedCallId: number | null
  onConfirmCall: (id: number | null) => void
  receiptOn: boolean
  onReceipt: (on: boolean) => void
  onExport: () => void
  exportView: ExportView
  downloadUrl: string | null
  /** The clip's caption file as SRT and WebVTT downloads; null for a created video. */
  captionUrls?: { srt: string; vtt: string } | null
  post: PostTarget | null
  /** "Fix before you post" (captions and post copy), shown above Export. */
  fixPanel?: ReactNode
  /** What the export would draw over the clip, shown on the player; null while loading or for a created video. */
  overlay?: ClipOverlay | null
  /** The receipt card's lines, when one will be attached. */
  receiptLines?: string[] | null
  /** Where "Follow the speaker" will crop this clip (the export's analysis), on request. */
  loadCrop?: (start: number, end: number) => Promise<CropPath>
}

const CAPTION_STYLES: { id: CaptionStyle; label: string }[] = [
  { id: 'classic', label: 'Classic' },
  { id: 'highlight', label: 'Highlight the word said' },
  { id: 'boxed', label: 'Boxed' },
]

const FORMATS: { id: ClipFormat; label: string }[] = [
  { id: 'vertical_speaker', label: 'Follow the speaker' },
  { id: 'vertical', label: 'Centred 9:16' },
  { id: 'original', label: 'Original shape' },
]

const presetActive = (preset: Preset, options: PanelOptions) =>
  preset.format === options.format &&
  preset.captions === options.captions &&
  preset.hook === options.hookOn &&
  preset.watermark === options.watermark

/** The right-hand panel: a phone-shaped player of the selected clip and its export options. */
const ClipPreview = forwardRef<ClipPreviewHandle, ClipPreviewProps>(function ClipPreview(
  {
    mediaUrl,
    title,
    range,
    postCopy,
    created,
    options,
    onOptions,
    hookText,
    onHookText,
    matches,
    confirmedCallId,
    onConfirmCall,
    receiptOn,
    onReceipt,
    onExport,
    exportView,
    downloadUrl,
    captionUrls,
    post,
    fixPanel,
    overlay,
    receiptLines,
    loadCrop,
  },
  ref
) {
  const video = useRef<HTMLVideoElement>(null)
  // The speaker crop, once asked for: for this clip's range only.
  const [crop, setCrop] = useState<
    { key: string; state: 'loading' } | { key: string; state: 'ready'; result: CropPath } | { key: string; state: 'failed' } | null
  >(null)
  const [videoSize, setVideoSize] = useState<{ w: number; h: number } | null>(null)
  const cropKey = range ? `${mediaUrl}|${range.start}|${range.end}` : ''
  const cropHere = crop && crop.key === cropKey ? crop : null
  const following = options.format === 'vertical_speaker' && !created
  const showCrop = async () => {
    if (!loadCrop || !range) return
    const key = cropKey
    setCrop({ key, state: 'loading' })
    try {
      setCrop({ key, state: 'ready', result: await loadCrop(range.start, range.end) })
    } catch {
      setCrop({ key, state: 'failed' })
    }
  }
  const cropPath = following && cropHere?.state === 'ready' ? cropHere.result.path : null
  const stopAt = useRef<number | null>(null)
  // While playing a clip, the sections cut out of it are skipped.
  const skip = useRef<Range['cuts']>([])
  // The player's time, for the preview overlay: followed every frame while playing.
  const [time, setTime] = useState<number | null>(null)
  const frame = useRef<number | null>(null)
  const follow = () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current)
    const tick = () => {
      const el = video.current
      if (!el || el.paused) {
        frame.current = null
        return
      }
      setTime(el.currentTime)
      frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)
  }
  useEffect(() => () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current)
  }, [])
  const [copied, setCopied] = useState(false)

  const play = (r: Range) => {
    const el = video.current
    if (!el) return
    stopAt.current = r.end
    skip.current = r.cuts || []
    el.currentTime = r.start
    el.play?.()?.catch?.(() => undefined)
  }
  useImperativeHandle(ref, () => ({ play }))

  // Show the clip's first frame when another clip is picked.
  useEffect(() => {
    if (video.current && range) {
      video.current.pause?.()
      stopAt.current = range.end
      video.current.currentTime = range.start
    }
    setTime(range ? range.start : null)
  }, [range?.start, range?.end]) // eslint-disable-line react-hooks/exhaustive-deps

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(postCopyText(postCopy))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }

  const busy = exportView.state === 'busy'
  const busyRecord = exportView.state === 'busy' ? exportView.record : null
  const progress = busyRecord?.status === 'processing' ? Math.round((busyRecord.progress || 0) * 100) : null
  const confirmed = matches.find((m) => m.id === confirmedCallId) || null

  return (
    <aside className="min-w-0 bg-white rounded-lg shadow p-5" aria-label="Preview">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Preview · 9:16</h3>
      <div className="mx-auto mt-4 w-full max-w-[240px] rounded-[2rem] border-8 border-gray-900 bg-black shadow-lg">
        <div className="relative aspect-[9/16] overflow-hidden rounded-[1.4rem] bg-black">
          {mediaUrl && range ? (
            <video
              ref={video}
              src={mediaUrl}
              preload="metadata"
              playsInline
              controls
              data-testid="clip-preview-video"
              onTimeUpdate={(e) => {
                const now = e.currentTarget.currentTime
                const cut = stopAt.current !== null ? (skip.current || []).find((c) => now >= c.start && now < c.end) : null
                if (cut) e.currentTarget.currentTime = cut.end
                if (stopAt.current !== null && e.currentTarget.currentTime >= stopAt.current) {
                  e.currentTarget.pause()
                  stopAt.current = null
                }
                setTime(e.currentTarget.currentTime)
              }}
              onPlay={follow}
              onSeeked={(e) => setTime(e.currentTarget.currentTime)}
              onLoadedMetadata={(e) => setVideoSize({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight })}
              style={
                cropPath && videoSize
                  ? {
                      objectPosition: `${cropObjectX(
                        cropCentreAt(cropPath, time ?? range.start),
                        videoSize.w,
                        videoSize.h
                      ).toFixed(2)}% 50%`,
                    }
                  : undefined
              }
              data-crop={cropPath ? 'speaker' : 'centre'}
              className={`h-full w-full ${options.format === 'original' && !created ? 'object-contain' : 'object-cover'}`}
            />
          ) : null}
          {mediaUrl && range && overlay && !created && (
            <PreviewOverlay
              overlay={overlay}
              time={time ?? range.start}
              elapsed={keptElapsed(Math.max(time ?? range.start, range.start), range)}
              captions={options.captions}
              captionStyle={options.captionStyle}
              watermark={options.watermark}
              hook={options.hookOn ? hookText.trim() || null : null}
              receiptLines={receiptLines || null}
            />
          )}
          {!(mediaUrl && range) && (
            <div className="flex h-full items-center justify-center p-4 text-center text-xs text-gray-400">
              Pick a clip to preview it
            </div>
          )}
        </div>
      </div>
      {overlay && range && !created && (
        <p className="mt-2 text-center text-xs text-gray-500" data-testid="preview-note">
          Captions and branding as the export draws them. Long text may wrap a little differently.
        </p>
      )}
      {following && range && mediaUrl && loadCrop && (
        <div className="mt-1 text-center text-xs" data-testid="crop-preview">
          {!cropHere && (
            <button type="button" onClick={showCrop} className="text-primary-600 hover:underline">
              Preview the speaker crop
            </button>
          )}
          {cropHere?.state === 'loading' && <span className="text-gray-500">Finding the speaker…</span>}
          {cropHere?.state === 'ready' &&
            (cropHere.result.path ? (
              <span className="text-gray-500">The crop follows the speaker as the export will.</span>
            ) : (
              <span className="text-gray-500">{cropHere.result.note}</span>
            ))}
          {cropHere?.state === 'failed' && (
            <button type="button" onClick={showCrop} className="text-danger-700 hover:underline">
              Couldn't follow the speaker. Try again
            </button>
          )}
        </div>
      )}
      {range && (
        <p className="mt-3 text-center text-sm text-gray-700">
          <span className="font-medium text-gray-900">{title}</span>
          <br />
          {formatTime(range.start)} – {formatTime(range.end)}
        </p>
      )}

      {!created && (
        <div className="mt-4 space-y-3 text-sm text-gray-700">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Presets">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={presetActive(p, options)}
                onClick={() => onOptions({ format: p.format, captions: p.captions, hookOn: p.hook, watermark: p.watermark })}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  presetActive(p, options)
                    ? 'border-primary-600 bg-primary-50 text-primary-600'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={options.captions}
              onChange={(e) => onOptions({ captions: e.target.checked })}
            />
            Burned-in captions
          </label>
          {options.captions && (
            <label className="flex items-center gap-2 pl-6">
              Style
              <select
                aria-label="Caption style"
                value={options.captionStyle}
                onChange={(e) => onOptions({ captionStyle: e.target.value as CaptionStyle })}
                className="rounded border border-gray-300 px-1.5 py-0.5 text-sm"
              >
                {CAPTION_STYLES.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div role="radiogroup" aria-label="Shape" className="space-y-1">
            {FORMATS.map((f) => (
              <label key={f.id} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="clip-format"
                  className="h-4 w-4"
                  checked={options.format === f.id}
                  onChange={() => onOptions({ format: f.id })}
                />
                {f.label}
              </label>
            ))}
          </div>

          <fieldset className="space-y-1">
            <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">Branding</legend>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={options.watermark}
                onChange={(e) => onOptions({ watermark: e.target.checked })}
              />
              FinCreator watermark
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={options.chart}
                onChange={(e) => onOptions({ chart: e.target.checked })}
              />
              <span>
                Chart when you say a price
                <span className="block text-xs text-gray-500">
                  The coin&rsquo;s real market for the 48 hours up to the upload, for a few seconds.
                </span>
                {options.chart && overlay?.chart_note && (
                  <span className="block text-xs text-amber-700" data-testid="chart-note">
                    {overlay.chart_note}
                  </span>
                )}
              </span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={options.introOutro}
                onChange={(e) => onOptions({ introOutro: e.target.checked })}
              />
              <span>
                Intro and end card
                <span className="block text-xs text-gray-500">
                  1.5 s crypto-rain intro, 2 s end card with fincreator.org and "Not financial advice".
                </span>
              </span>
            </label>
          </fieldset>

          <div className="space-y-1">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={options.hookOn}
                onChange={(e) => onOptions({ hookOn: e.target.checked })}
              />
              Hook text (first 3 s)
            </label>
            {options.hookOn && (
              <input
                type="text"
                aria-label="Hook text"
                value={hookText}
                maxLength={120}
                onChange={(e) => onHookText(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
              />
            )}
          </div>

          {matches.length > 0 && (
            <fieldset className="space-y-2 rounded-md border border-gray-300 p-3" data-testid="receipt-options">
              <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Receipt card</legend>
              <p className="text-xs text-gray-500">This clip mentions a trade that matches a locked call. Check it before attaching:</p>
              {matches.map((m) => (
                <label key={m.id} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4"
                    checked={confirmedCallId === m.id}
                    onChange={(e) => onConfirmCall(e.target.checked ? m.id : null)}
                  />
                  <span>
                    Yes, this clip is about {m.receipt_path}
                    <span className="block break-words text-xs text-gray-500">{m.card_text}</span>
                  </span>
                </label>
              ))}
              <label className={`flex items-center gap-2 ${confirmed ? '' : 'opacity-50'}`}>
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={receiptOn && !!confirmed}
                  disabled={!confirmed}
                  onChange={(e) => onReceipt(e.target.checked)}
                />
                Attach receipt card
              </label>
            </fieldset>
          )}
        </div>
      )}

      {fixPanel && <div className="mt-4">{fixPanel}</div>}

      <div className="mt-4 flex flex-col gap-2">
        {exportView.state === 'ready' && downloadUrl ? (
          <a
            href={downloadUrl}
            download
            className="rounded-md bg-success-600 px-4 py-2 text-center text-sm font-medium text-white hover:bg-success-700"
          >
            Download MP4
          </a>
        ) : exportView.state === 'stale' ? (
          <div className="space-y-2" data-testid="export-stale">
            <p className="text-sm text-gray-700">This export is out of date: the clip's settings changed since it was made.</p>
            <button
              type="button"
              onClick={onExport}
              className="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              Export again
            </button>
          </div>
        ) : !created ? (
          <button
            type="button"
            onClick={onExport}
            disabled={!range || busy}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? (progress !== null ? `Exporting… ${progress}%` : 'Exporting…') : 'Export MP4'}
          </button>
        ) : null}
        {busy && (
          <div
            className="h-1.5 w-full overflow-hidden rounded bg-gray-200"
            role="progressbar"
            aria-label="Export progress"
            aria-valuenow={progress ?? 0}
          >
            <div className="h-full bg-primary-600 transition-all" style={{ width: `${progress ?? 5}%` }} />
          </div>
        )}
        {exportView.state === 'failed' && (
          <p role="alert" className="text-sm text-danger-600">Export failed: {exportView.record.error || 'unknown error'}</p>
        )}
        {exportView.state === 'ready' && exportView.record.note && (
          <p className="text-xs text-gray-500">{exportView.record.note}</p>
        )}
        <button
          type="button"
          onClick={copy}
          disabled={!postCopy}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          {copied ? 'Copied' : 'Copy caption'}
        </button>
        {captionUrls && (
          <p className="text-center text-xs text-gray-500" data-testid="clip-caption-files">
            Caption file for this clip:{' '}
            <a href={captionUrls.srt} download className="font-medium text-primary-600 hover:underline">
              SRT
            </a>{' '}
            ·{' '}
            <a href={captionUrls.vtt} download className="font-medium text-primary-600 hover:underline">
              VTT
            </a>
          </p>
        )}
        {post && (
          <PostPanel
            target={post}
            postCopy={postCopy}
            downloadUrl={exportView.state === 'ready' ? downloadUrl : null}
            exportId={exportView.state === 'ready' && !created ? exportView.record.id : null}
            defaultPlatform={!created && options.format === 'original' ? 'twitter' : 'tiktok'}
          />
        )}
      </div>
    </aside>
  )
})

export default ClipPreview
